import { useState, useEffect } from 'react';
import { getConversations, getConversation, deleteConversation, type ConversationSummary } from '../../services/agentApi';

interface ConversationHistoryProps {
    onSelect: (conversationId: string) => void;
    onClose: () => void;
    currentConversationId: string | null;
}

function formatRelativeDate(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
}

export default function ConversationHistory({ onSelect, onClose, currentConversationId }: ConversationHistoryProps) {
    const [conversations, setConversations] = useState<ConversationSummary[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    useEffect(() => {
        loadConversations();
    }, []);

    async function loadConversations() {
        setIsLoading(true);
        try {
            const data = await getConversations();
            setConversations(data);
        } catch (err) {
            console.error('Failed to load conversations:', err);
        } finally {
            setIsLoading(false);
        }
    }

    async function handleDelete(e: React.MouseEvent, conversationId: string) {
        e.stopPropagation();
        setDeletingId(conversationId);
        try {
            await deleteConversation(conversationId);
            setConversations(prev => prev.filter(c => c.conversation_id !== conversationId));
        } catch (err) {
            console.error('Failed to delete conversation:', err);
        } finally {
            setDeletingId(null);
        }
    }

    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <div className="text-sm text-slate-400">Loading conversations...</div>
            </div>
        );
    }

    if (conversations.length === 0) {
        return (
            <div className="flex-1 flex items-center justify-center p-6">
                <div className="text-center">
                    <div className="text-slate-400 mb-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                    </div>
                    <p className="text-sm text-slate-500">No conversations yet</p>
                    <p className="text-xs text-slate-400 mt-1">Start a new chat to begin</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 overflow-y-auto">
            {conversations.map(conv => (
                <button
                    key={conv.conversation_id}
                    onClick={() => onSelect(conv.conversation_id)}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors group ${
                        conv.conversation_id === currentConversationId ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                    }`}
                >
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-700 truncate">
                                {conv.title || 'Untitled conversation'}
                            </p>
                            <p className="text-xs text-slate-400 mt-0.5">
                                {formatRelativeDate(conv.updated_at)}
                            </p>
                        </div>
                        <button
                            onClick={(e) => handleDelete(e, conv.conversation_id)}
                            disabled={deletingId === conv.conversation_id}
                            className="opacity-0 group-hover:opacity-100 p-1 text-slate-300 hover:text-red-500 transition-all shrink-0"
                            title="Delete conversation"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>
                </button>
            ))}
        </div>
    );
}
